// Libellés français des compétences fournies par la plateforme (compétences globales et système,
// définies dans front/lib/resources/skill/code_defined/). On les traduit À L'AFFICHAGE seulement :
// le nom et les descriptions envoyés au modèle restent ceux d'origine. Les compétences métier des
// apps (sId « skl_… ») sont traduites par leur nom d'origine (libelles_competences_metier.ts, propre à chaque app) ; une
// compétence créée par un membre s'affiche telle qu'elle a été écrite.
// Pas de tiret long ni de nom de marque dans un texte affiché.

import { libelleCompetenceMetier } from "@app/lib/compta/libelles_competences_metier";

interface LibelleCompetence {
  // Nom d'origine, celui que voit le modèle (et qu'il renvoie dans ses appels d'outils).
  nomOrigine: string;
  nom: string;
  description: string;
}

export const LIBELLES_COMPETENCES_GLOBALES: Record<string, LibelleCompetence> =
  {
    dust_learning: {
      nomOrigine: "Dust Learning",
      nom: "Conseils d'utilisation",
      description:
        "Recevoir une recommandation pour tirer davantage parti de l'assistant.",
    },
    "conversational-building": {
      nomOrigine: "Build Agents and Skills",
      nom: "Créer des assistants et des compétences",
      description:
        "Laisser l'assistant proposer, à partir d'une conversation, des améliorations de vos compétences, soumises à leurs éditeurs.",
    },
    docx: {
      nomOrigine: "Documents",
      nom: "Documents",
      description: "Lire, modifier et créer des documents texte (.docx).",
    },
    frames: {
      nomOrigine: "Create Frames",
      nom: "Créer des tableaux de bord",
      description:
        "Transformer des analyses en tableaux de bord et présentations interactifs, à explorer, adapter et partager avec l'équipe.",
    },
    "go-deep": {
      nomOrigine: "Go Deep",
      nom: "Analyse approfondie",
      description:
        "Analyser en profondeur les données de l'entreprise, les bases et le web. L'analyse peut prendre plusieurs minutes.",
    },
    dust_pod_goal: {
      nomOrigine: "Dust Pod Goal",
      nom: "Objectif du projet",
      description:
        "Faire avancer la mission d'un projet avec la prochaine action, appuyée sur des éléments concrets.",
    },
    markdown_documents: {
      nomOrigine: "Markdown Document Edition",
      nom: "Documents Markdown",
      description:
        "Rédiger et modifier des documents Markdown qui s'ouvrent dans l'éditeur, avec des images, des tableaux de bord et des fichiers de la conversation ou du projet, et répondre à leurs commentaires.",
    },
    mention_users: {
      nomOrigine: "Mention Users",
      nom: "Mentionner des membres",
      description:
        "Permettre à l'assistant de mentionner et prévenir des membres dans une conversation.",
    },
    pdf: {
      nomOrigine: "PDFs",
      nom: "PDF",
      description:
        "Lire des PDF (y compris scannés), en extraire le texte et les tableaux, en créer et en modifier.",
    },
    pptx: {
      nomOrigine: "Slide decks",
      nom: "Présentations",
      description: "Lire, modifier et créer des présentations.",
    },
    projects: {
      nomOrigine: "Pods",
      nom: "Projets",
      description:
        "Permettre à l'assistant de créer des conversations et des messages dans les projets, et d'en exploiter le contenu.",
    },
    sandbox: {
      nomOrigine: "Computer",
      nom: "Ordinateur",
      description:
        "Exécuter du code, des scripts et des commandes dans l'ordinateur de la conversation (un environnement Linux isolé).",
    },
    schedules: {
      nomOrigine: "Schedules",
      nom: "Tâches planifiées",
      description:
        "Créer des tâches planifiées et des déclencheurs pour automatiser les tâches récurrentes ou liées à un événement.",
    },
    "sheet-to-frame": {
      nomOrigine: "Sheet to Frame",
      nom: "Du tableur à l'application",
      description:
        "Transformer un tableur de saisie en application : un formulaire qui préremplit l'évident, des formules qui deviennent des champs calculés, et les lignes existantes reprises.",
    },
    "skill-authoring": {
      nomOrigine: "Author Skills",
      nom: "Créer des compétences",
      description:
        "Laisser l'assistant créer et affiner des compétences réutilisables.",
    },
    support: {
      nomOrigine: "Dust Support",
      nom: "Assistance",
      description:
        "Obtenir de l'aide sur l'assistant à partir de la documentation publique.",
    },
    "workspace-analytics": {
      nomOrigine: "Workspace Analytics",
      nom: "Analytique de l'espace",
      description:
        "Analyser l'usage de l'espace de travail : qui et quoi consomme et fait vivre l'activité, chiffres clés et tendances. Recense aussi les assistants et les compétences.",
    },
    // Compétences système (front/lib/resources/skill/code_defined/system/) : mêmes libellés que i18n.ts.
    discover_knowledge: {
      nomOrigine: "Discover Knowledge",
      nom: "Explorer les connaissances",
      description:
        "Chercher dans tous les documents et données de l'entreprise pour trouver l'information utile, sans rien configurer.",
    },
    discover_skills: {
      nomOrigine: "Discover Skills",
      nom: "Découvrir des compétences",
      description:
        "Trouver et activer au besoin les compétences de l'espace de travail.",
    },
    discover_tools: {
      nomOrigine: "Discover Tools",
      nom: "Découvrir des outils",
      description:
        "Trouver et activer au besoin des outils spécialisés, sans rien configurer.",
    },
    plan_mode: {
      nomOrigine: "Plan Mode",
      nom: "Mode plan",
      description:
        "Tenir à jour un plan de travail que vous suivez au fil de l'avancement.",
    },
    user_memory: {
      nomOrigine: "User Memory",
      nom: "Mémoire de l'utilisateur",
      description:
        "Garder en mémoire vos préférences et votre contexte d'une conversation à l'autre.",
    },
    xlsx: {
      nomOrigine: "Spreadsheets",
      nom: "Tableurs",
      description: "Lire, modifier et créer des tableurs (.xlsx, .csv).",
    },
  };

/** Nom affiché d'une compétence : traduit pour une compétence de la plateforme, d'origine sinon. */
export function nomCompetence(competence: {
  sId: string;
  name: string;
}): string {
  return (
    LIBELLES_COMPETENCES_GLOBALES[competence.sId]?.nom ??
    libelleCompetenceMetier(competence.name)?.nom ??
    competence.name
  );
}

/** Description affichée d'une compétence : même règle que le nom. */
export function descriptionCompetence(competence: {
  sId: string;
  userFacingDescription: string;
  // Le nom d'origine, pour les compétences métier (traduites par leur nom) ; sans lui, seule la
  // table des compétences de la plateforme s'applique.
  name?: string;
}): string {
  return (
    LIBELLES_COMPETENCES_GLOBALES[competence.sId]?.description ??
    (competence.name !== undefined
      ? libelleCompetenceMetier(competence.name)?.description
      : undefined) ??
    competence.userFacingDescription
  );
}

/**
 * Nom affiché depuis le seul nom d'origine (étape « Activation de la compétence » d'un message, où
 * l'appel d'outil ne porte que le nom). Les noms des compétences de la plateforme et des compétences
 * métier sont traduits ; tout autre nom est rendu tel quel.
 */
export function nomCompetenceDepuisNom(nom: string): string {
  for (const libelle of Object.values(LIBELLES_COMPETENCES_GLOBALES)) {
    if (libelle.nomOrigine === nom) {
      return libelle.nom;
    }
  }
  const metier = libelleCompetenceMetier(nom);
  if (metier) {
    return metier.nom;
  }
  return nom;
}
